import * as R from 'ramda';
import type { KeyParametersPartsFragment } from '~/apollo/generated/v4/graphql';
import { GeologyType } from '~/apollo/generated/v4/graphql';
import { assertExhaustive, rejectNil } from '~/utils/common';

type KPFieldName = keyof Pick<
  KeyParametersPartsFragment,
  | 'grossDepositionalEnvironment'
  | 'depositionalEnvironment'
  | 'depositionalSubEnvironment'
  | 'architecturalElement'
>;

export const kpFieldName =
  (geologyType: GeologyType | null) =>
  (field: KPFieldName): string => {
    if (geologyType === GeologyType.Structural) {
      switch (field) {
        case 'grossDepositionalEnvironment':
          return 'Structure class';
        case 'depositionalEnvironment':
          return 'Structure type';
        case 'depositionalSubEnvironment':
          return 'Structure style';
        case 'architecturalElement':
          return 'Architectural element';
        default:
          throw assertExhaustive(field);
      }
    }

    switch (field) {
      case 'grossDepositionalEnvironment':
        return 'Gross depositional environment';
      case 'depositionalEnvironment':
        return 'Depositional environment';
      case 'depositionalSubEnvironment':
        return 'Subenvironment';
      case 'architecturalElement':
        return 'Architectural element';
      default:
        throw assertExhaustive(field);
    }
  };

export const kpFieldAbbr =
  (geologyType: GeologyType | null) =>
  (field: KPFieldName): string => {
    if (geologyType === GeologyType.Structural) {
      switch (field) {
        case 'grossDepositionalEnvironment':
          return 'Structure class';
        case 'depositionalEnvironment':
          return 'Structure type';
        case 'depositionalSubEnvironment':
          return 'Structure style';
        case 'architecturalElement':
          return 'Arch. Element';
        default:
          throw assertExhaustive(field);
      }
    }

    switch (field) {
      case 'grossDepositionalEnvironment':
        return 'GDE';
      case 'depositionalEnvironment':
        return 'DE';
      case 'depositionalSubEnvironment':
        return 'SE';
      case 'architecturalElement':
        return 'AE';
      default:
        throw assertExhaustive(field);
    }
  };

export interface MergedKeyParameters {
  grossDepositionalEnvironment: string[];
  depositionalEnvironment: string[];
  depositionalSubEnvironment: string[];
  architecturalElement: string[];
  diageneticProduct: string[];
}

type KPParts = {
  grossDepositionalEnvironment?: string | null;
  depositionalEnvironment?: string | null;
  depositionalSubEnvironment?: string | null;
  architecturalElement?: string | null;
};
type KPArg = KPParts[] | KPParts[][];
type ParamName = keyof KPParts;

const uniqueParams =
  (keyParameters: KPArg = []) =>
  (paramName: ParamName): string[] => {
    const flattened = R.flatten(keyParameters).filter(rejectNil);

    // When looking at AEs, remove diagenetic products
    const withoutDPs = flattened.filter(kps =>
      paramName === 'architecturalElement'
        ? kps.depositionalSubEnvironment !== 'Diagenetic product'
        : true,
    );

    const plucked = withoutDPs.map(kps => kps[paramName]);

    // If looking at SEs, remove the 'diagenetic product' option
    const withoutDPLabel = plucked.filter(item =>
      paramName === 'depositionalSubEnvironment'
        ? item !== 'Diagenetic product'
        : true,
    );

    const nonNulls = withoutDPLabel.filter(
      (item): item is NonNullable<string> => !!item,
    );

    return R.pipe(R.uniq, R.sortBy(R.identity))(nonNulls);
  };

const diageneticProducts = (keyParameters: KPArg = []): string[] => {
  const dps = R.flatten(keyParameters)
    .filter(kp => kp.depositionalSubEnvironment === 'Diagenetic product')
    .map(kp => kp.architecturalElement)
    .filter((ae): ae is NonNullable<string> => !!ae);

  return R.pipe(R.uniq, R.sortBy(R.identity))(dps);
};

export function mergeKeyParameters(
  keyParameters: KPArg = [],
): MergedKeyParameters {
  const mergeParams = uniqueParams(keyParameters);
  return {
    grossDepositionalEnvironment: mergeParams('grossDepositionalEnvironment'),
    depositionalEnvironment: mergeParams('depositionalEnvironment'),
    depositionalSubEnvironment: mergeParams('depositionalSubEnvironment'),
    architecturalElement: mergeParams('architecturalElement'),
    diageneticProduct: diageneticProducts(keyParameters),
  };
}
